import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import type { GlobalDataLookupSlice } from "./types";
import {
  useAccountingSystemsLookup,
  useAdjustmentCategoriesLookup,
  useDeductionPrioritiesLookup,
  useDeductionStagesLookup,
  useDeductionValiditiesLookup,
  useDeductionValidityRcasLookup,
  useFeaturesLookup,
  useOrganizationsBrandsLookup,
} from "./globalDataLookup";

const GlobalDataLookupContext =
  createContext<GlobalDataLookupSlice | null>(null);

function useGlobalDataLookupContext(): GlobalDataLookupSlice {
  const value = useContext(GlobalDataLookupContext);
  if (value === null) {
    throw new Error(
      "useGlobalDataLookup must be used within a GlobalDataLookupProvider",
    );
  }
  return value;
}

/**
 * Hook to read/use global lookup data (features, orgs/brands, accounting systems).
 */
export function useGlobalDataLookup(): GlobalDataLookupSlice {
  return useGlobalDataLookupContext();
}

interface GlobalDataLookupProviderProps {
  children: ReactNode;
}

export function GlobalDataLookupProvider({
  children,
}: Readonly<GlobalDataLookupProviderProps>) {
  const features = useFeaturesLookup();
  const organizationsBrands = useOrganizationsBrandsLookup();
  const accountingSystems = useAccountingSystemsLookup();
  const adjustmentCategories = useAdjustmentCategoriesLookup();
  const deductionPriorities = useDeductionPrioritiesLookup();
  const deductionStages = useDeductionStagesLookup();
  const deductionValidities = useDeductionValiditiesLookup();
  const deductionValidityRcas = useDeductionValidityRcasLookup();

  const value = useMemo<GlobalDataLookupSlice>(
    () => ({
      ...features,
      ...organizationsBrands,
      ...accountingSystems,
      ...adjustmentCategories,
      ...deductionPriorities,
      ...deductionStages,
      ...deductionValidities,
      ...deductionValidityRcas,
    }),
    [
      features,
      organizationsBrands,
      accountingSystems,
      adjustmentCategories,
      deductionPriorities,
      deductionStages,
      deductionValidities,
      deductionValidityRcas,
    ],
  );

  return (
    <GlobalDataLookupContext.Provider value={value}>
      {children}
    </GlobalDataLookupContext.Provider>
  );
}
